import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../app/store";

import {
  getPartnersFromServer,
  createPartnerServer,
  editPartnerServer,
  createPartnerTeamMemberServer,
  getPartnerMarkupReportFromServer,
  IGetPartnersResponse,
} from "./partnersApi";
import {
  IPartner,
  IPartnerTeamMember,
  PartnerType,
} from "../../interfaces/partner.interface";
import { IPartnerDeal } from "../../interfaces/partnerDeal.interface";
import {
  IPartnerReportRequest,
  IReportRequest,
} from "../../interfaces/requestReport.interface";
import { PartnerReportType } from "../../interfaces/reports.interface";
import { stat } from "fs";

// import { fetchCount } from './counterAPI';

export interface PartnerState {
  partners: IPartner[];
  currentPartner: IPartner;
  totalCount: number;
  pageSize: number;
  currentPage: number;
}

const initialState: PartnerState = {
  partners: [] as IPartner[],
  currentPartner: {} as IPartner,
  totalCount: 0,
  pageSize: 10,
  currentPage: 1,
};

export const getPartnersThunk = createAsyncThunk(
  "partners/getAll",
  async (
    params?: Partial<{
      partnerNames?: string;
      name?: string;
      partnerType?: PartnerType | string;
      currentPage?: number;
      pageSize?: number;
      country?: string;
    }>,
  ): Promise<IGetPartnersResponse> => {
    const response = await getPartnersFromServer(params);
    return response;
  },
);
export const getPartnerThunk = createAsyncThunk(
  "partners/get",
  async (partnerId: string) => {
    const response = await getPartnersFromServer({ partnerId });

    return response?.data?.length > 0 ? response?.data[0] : null;
  },
);

export const createPartnerThunk = createAsyncThunk(
  "partners/create",
  async (partner: Partial<IPartner>) => {
    const response = await createPartnerServer(partner);
    return response;
  },
);
export const createPartnerTeamMemberThunk = createAsyncThunk(
  "partners/teamMembers/create",
  async (params: {
    teamMember: Partial<IPartnerTeamMember>;
    partnerId: string;
  }) => {
    const response = await createPartnerTeamMemberServer(params);
    return response;
  },
);
// `update` accepts either a `Partial<IPartner>` for shape-typed writes or a
// `Record<string, unknown>` for Mongo-style dotted-path writes (e.g.
// `defaultOnlinePricing.iDeal`). Trade-off: dotted-path keys can't be
// type-checked for typos. Use shape-typed when possible.
export const editPartnerThunk = createAsyncThunk(
  "partners/edit",
  async (params: {
    partnerId: string;
    update: Partial<IPartner> | Record<string, unknown>;
  }) => {
    const response = await editPartnerServer(params);
    return response;
  },
);
export const getPartnerMarkupReportThunk = createAsyncThunk(
  "partners/markup",
  async (reportRequest: IPartnerReportRequest) => {
    const response = await getPartnerMarkupReportFromServer(reportRequest);
    return response;
  },
);

export const partnersSlice = createSlice({
  name: "partners",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setCurrentPartner: (state, action: PayloadAction<string>) => {
      let partner = state.partners.find((p) => p._id === action.payload);
      if (partner) state.currentPartner = partner;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder

      .addCase(getPartnersThunk.fulfilled, (state, action) => {
        state.partners = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.pageSize = action.payload.pageSize;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(getPartnerThunk.fulfilled, (state, action) => {
        if (action.payload) state.currentPartner = action.payload;
      })
      .addCase(createPartnerThunk.fulfilled, (state, action) => {
        state.partners.push(action.payload);
      })

      .addCase(createPartnerTeamMemberThunk.fulfilled, (state, action) => {
        state.partners = state.partners.map((p) =>
          p._id === action.payload._id ? action.payload : p,
        );

        state.currentPartner = action.payload;
      })
      .addCase(editPartnerThunk.fulfilled, (state, action) => {
        state.partners = state.partners.map((p) =>
          p._id === action.payload._id ? action.payload : p,
        );
        state.currentPartner = action.payload;
      });
  },
});

export const { setCurrentPartner } = partnersSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`

export const selectPartners = (state: RootState) => state.partners.partners;
export const selectTotalPartnersCount = (state: RootState) =>
  state.partners.totalCount;
export const selectPartnersPageSize = (state: RootState) =>
  state.partners.pageSize;
export const selectPartnersCurrentPage = (state: RootState) =>
  state.partners.currentPage;
export const selectSoftwareVendors = (state: RootState) =>
  state.partners.partners.filter(
    (p) =>
      p.partnerType === PartnerType.SoftwareVendor ||
      p.partnerType === PartnerType.FullServiceWithResellers,
  );
export const selectCurrentPartner = (state: RootState) =>
  state.partners.currentPartner;

export default partnersSlice.reducer;
